import React, { useMemo } from "react";
import Section from "./section";
import RtValue from "./rtValue";
import { IStrandCodes } from "./types";
import { IRealtimeData } from "../../../../types";
import { DashboardSchemaType, IAcquisitionDataSchema } from "../../../interruptions/types";
import { IDashboard } from "../../../dashboardRT/types";
import AcquisitionChartWrapper from "../../../dashboardRT/components/widgets/chart/acquisitionChartWrapper";

interface IProps {
  title: string;
  strandCode: string;
  realtime:  Record<string, IRealtimeData>;
  width: number;
}

const schemas: Record<string, IAcquisitionDataSchema> = {
  [DashboardSchemaType.Acquisition]: {
    variables: {},
    events: {}
  }
};

const strandCodes = (code: string): IStrandCodes => ({
  speed: {
    value: `MS.CASTER1.MOULD.LEVEL.${code}.ACTUAL`,
    actual: `MS.CASTER1.MOULD.LEVEL.${code}.SPEED_ACTUAL`,
    sp: `MS.CASTER1.MOULD.LEVEL.${code}.SP`
  },
  mouldCooling: {
    flow: {
      actual: `MS.CASTER1.MOULD.COOLING.${code}.FLOW_ACTUAL`,
      aimed: `MS.CASTER1.MOULD.COOLING.${code}.FLOW_AIME`
    },
    temperature: {
      in: `MS.CASTER1.MOULD.COOLING.${code}.TEMP_IN`,
      out: `MS.CASTER1.MOULD.COOLING.${code}.TEMP_OUT`,
      delta: `MS.CASTER1.MOULD.COOLING.${code}.TEMP_DELTA_T`
    }
  },
  sprayCooling: {
    zone1: {
      flow: {
        sp: `MS.CASTER1.SPRAY.COOLING.${code}.Z1.FLOW_SP`,
        actual: `MS.CASTER1.SPRAY.COOLING.${code}.Z1.FLOW_ACTUAL`
      },
      pressure: `MS.CASTER1.SPRAY.COOLING.${code}.Z1.PRESSURE_ACTUAL`
    },
    zone2: {
      flow: {
        sp: `MS.CASTER1.SPRAY.COOLING.${code}.Z2.FLOW_SP`,
        actual: `MS.CASTER1.SPRAY.COOLING.${code}.Z2.FLOW_ACTUAL`
      },
      pressure: `MS.CASTER1.SPRAY.COOLING.${code}.Z2.PRESSURE_ACTUAL`
    },
    zone3: {
      flow: {
        sp: `MS.CASTER1.SPRAY.COOLING.${code}.Z3.FLOW_SP`,
        actual: `MS.CASTER1.SPRAY.COOLING.${code}.Z3.FLOW_ACTUAL`
      },
      pressure: `MS.CASTER1.SPRAY.COOLING.${code}.Z3.PRESSURE_ACTUAL`
    },
    zone4: {
      flow: {
        sp: `MS.CASTER1.SPRAY.COOLING.${code}.Z4.FLOW_SP`,
        actual: `MS.CASTER1.SPRAY.COOLING.${code}.Z4.FLOW_ACTUAL`
      },
      pressure: `MS.CASTER1.SPRAY.COOLING.${code}.Z4.PRESSURE_ACTUAL`
    }
  },
  cutter: {
    withdrawal: `MS.CASTER1.WITHDRAWAL.${code}.ACTUAL_PRESSURE`,
    straightener: `MS.CASTER1.STRAIGHTENER.${code}.ACTUAL_PRESSURE`,
    length: {
      actual: `MS.CASTER1.CUTTER.LENGTH.${code}.ACTUAL_LEN`,
      totalCasted: `MS.CASTER1.CUTTER.LENGTH.${code}.TOTAL_CASTED_LENGTH`,
      remaining: `MS.CASTER1.CUTTER.LENGTH.${code}.REMAINING_LENGTH`,
      totalCut: `MS.CASTER1.CUTTER.LENGTH.${code}.TOTAL_CUT`
    }
  }
});

const dashboardConfig = (code: string): IDashboard => ({
  dashboardID: null,
  moduleID: 2,
  nodeType: "",
  nodeID: 0,
  name: "",
  layout: {
    components:[
      {
        key: "ld4n9enfycyo2o8ic4",
        title: "Variables",
        componentType: 1,
        column: 1,
        row: 1,
        columnSpan: 1,
        rowSpan: 1,
        config: {
          acquisitionCell: {
            key: "ld4n9io2v50wiak3e7o",
            module: 2,
            title: "Name",
            machine: "EAF",
            variables: [
              {
                hash: "ld4nadw96zh1jnfwl4c",
                field: `MS.CASTER1.MOULD.LEVEL.${code}.ACTUAL`,
                title: "ACTUAL",
                color: "#31FF14",
                unit: "s ton",
                decimals: 2,
                limits: [],
                annotations: [],
                yAxis: {
                  name: "SPEED",
                  minimum: 60,
                  maximum: 150,
                  ticksCount: 5
                }
              },
              {
                hash: "ld4nadw93zh1jnfwl4c",
                field: `MS.CASTER1.MOULD.LEVEL.${code}.SP`,
                title: "SP",
                color: "#FFFFFF",
                unit: "s ton",
                decimals: 2,
                limits: [],
                annotations: [],
                yAxis: {
                  name: "SPEED",
                  minimum: 60,
                  maximum: 150,
                  ticksCount: 5
                }
              },
              {
                hash: "ld4nadw90zh1jnfwl4c",
                field: `MS.CASTER1.MOULD.LEVEL.${code}.SPEED_ACTUAL`,
                title: "SPEED",
                color: "#FF7878",
                unit: "s ton",
                decimals: 2,
                limits: [],
                annotations: [],
                yAxis: {
                  name: "SPEED",
                  minimum: 60,
                  maximum: 150,
                  ticksCount: 5
                }
              }
            ],
            events: []
          }
        }
      },
      {
        key: "ld4n9enfycyo2o8ic8",
        title: "Variables",
        componentType: 1,
        column: 1,
        row: 1,
        columnSpan: 1,
        rowSpan: 1,
        config: {
          acquisitionCell: {
            key: "ld4n9io2v50wiax3e7o",
            module: 2,
            title: "Name",
            machine: "EAF",
            variables: [
              {
                hash: "ld4nadw96zh1jnfwl4c",
                field: `MS.CASTER1.STRAIGHTENER.${code}.ACTUAL_PRESSURE`,
                title: "STRAIGHTENER",
                color: "#e1bc29",
                unit: "psi",
                decimals: 2,
                limits: [],
                annotations: [],
                yAxis: {
                  name: "PSI",
                  minimum: 300,
                  maximum: 500,
                  ticksCount: 5
                }
              },
              {
                hash: "ld4nadw93zh1jnfwl4c",
                field: `MS.CASTER1.WITHDRAWAL.${code}.ACTUAL_PRESSURE`,
                title: "WITHDRAWAL",
                color: "#3bb273",
                unit: "psi",
                decimals: 2,
                limits: [],
                annotations: [],
                yAxis: {
                  name: "PSI",
                  minimum: 300,
                  maximum: 500,
                  ticksCount: 5
                }
              }
            ],
            events: []
          }
        }
      }
    ],
    palette:{

    }
  },
  userID: 0,
  owner: null,
  isPrivate: true,
  isDefault: false,
  isAcquisition: true,
  customConfig: {
    range: 1,
    fontSize: 1,
    scan: 2000
  },
  rtModule: true
});


const StrandComponent = ({ title, strandCode, realtime, width }: IProps) => {
  const codes = useMemo(() => strandCodes(strandCode), [strandCode]);
  const dashboard = useMemo(() => dashboardConfig(strandCode), [strandCode]);

  return (
    <Section title={title} level={0}>
      <div className="display-grid" style={{ gridTemplateRows: "1fr auto" }}>
        <div>
          <AcquisitionChartWrapper
            realtime={realtime}
            component={dashboard.layout.components[0]}
            dashboard={dashboard}
            runtime={true}
            parentSize={{ width: width, height: 200 }}
            setDashboard={() => {}}
            schemas={schemas}/>
        </div>
        <div className="display-grid" style={{ gridTemplateRows: "auto auto 1fr" }}>
          <RtValue orientation="vertical" title="Speed [ipm]" rtValue={realtime[codes.speed.actual]} style={{ color: "red", textAlign: "center" }} />
          <Section title="Steel Level [%]" level={1}>
            <RtValue orientation="horizontal" title="SP" rtValue={realtime[codes.speed.sp]} style={{ color: "green" }} />
            <RtValue orientation="horizontal" title="Actual" rtValue={realtime[codes.speed.value]} style={{ color: "green" }} />
          </Section>
        </div>
      </div>
      <Section title="Mould Cooling" level={1}>
        <div className="display-grid gap-1" style={{ gridTemplateColumns: "1fr 1fr" }}>
          <Section title="Flow [gpm]" level={2}>
            <div className="display-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
              <RtValue orientation="vertical" title="Actual" rtValue={realtime[codes.mouldCooling.flow.actual]} style={{ color: "green" }} />
              <RtValue orientation="vertical" title="Aimed" rtValue={realtime[codes.mouldCooling.flow.aimed]} style={{ color: "blue" }} />
            </div>
          </Section>
          <Section title="Temperature [°F]" level={2}>
            <div className="display-grid" style={{ gridTemplateColumns: "1fr 1fr 1fr" }}>
              <RtValue orientation="vertical" title="In" rtValue={realtime[codes.mouldCooling.temperature.in]} style={{ color: "green" }} />
              <RtValue orientation="vertical" title="Out" rtValue={realtime[codes.mouldCooling.temperature.out]} style={{ color: "green" }} />
              <RtValue orientation="vertical" title="Delta T" rtValue={realtime[codes.mouldCooling.temperature.delta]} style={{ color: "green" }} />
            </div>
          </Section>
        </div>
      </Section>
      <Section title="Spray Cooling" level={1}>
        <div className="display-grid gap-1" style={{ gridTemplateColumns: "1fr 1fr", gridTemplateRows: "auto auto" }}>
          <Section title="Zone 1" level={2}>
            <div className="display-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
              <Section title="Flow [gpm]" level={3}>
                <div>
                  <RtValue orientation="horizontal" title="Sp" rtValue={realtime[codes.sprayCooling.zone1.flow.sp]} style={{ color: "blue" }} />
                  <RtValue orientation="horizontal" title="Actual" rtValue={realtime[codes.sprayCooling.zone1.flow.actual]} style={{ color: "green" }} />
                </div>
              </Section>
              <Section title="Pressure [psi]" level={3}>
                <RtValue orientation="horizontal" title="Sp" rtValue={realtime[codes.sprayCooling.zone1.pressure]} style={{ color: "green" }} />
                <span/>
              </Section>
            </div>
          </Section>
          <Section title="Zone 2" level={2}>
            <div className="display-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
              <Section title="Flow [gpm]" level={3}>
                <div>
                  <RtValue orientation="horizontal" title="Sp" rtValue={realtime[codes.sprayCooling.zone2.flow.sp]} style={{ color: "blue" }} />
                  <RtValue orientation="horizontal" title="Actual" rtValue={realtime[codes.sprayCooling.zone2.flow.actual]} style={{ color: "green" }} />
                </div>
              </Section>
              <Section title="Pressure [psi]" level={3}>
                <RtValue orientation="horizontal" title="Sp" rtValue={realtime[codes.sprayCooling.zone2.pressure]} style={{ color: "green" }} />
                <span/>
              </Section>
            </div>
          </Section>
          <Section title="Zone 3" level={2}>
            <div className="display-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
              <Section title="Flow [gpm]" level={3}>
                <div>
                  <RtValue orientation="horizontal" title="Sp" rtValue={realtime[codes.sprayCooling.zone3.flow.sp]} style={{ color: "blue" }} />
                  <RtValue orientation="horizontal" title="Actual" rtValue={realtime[codes.sprayCooling.zone3.flow.actual]} style={{ color: "green" }} />
                </div>
              </Section>
              <Section title="Pressure [psi]" level={3}>
                <RtValue orientation="horizontal" title="Sp" rtValue={realtime[codes.sprayCooling.zone3.pressure]} style={{ color: "green" }} />
                <span/>
              </Section>
            </div>
          </Section>
          <Section title="Zone 4" level={2}>
            <div className="display-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
              <Section title="Flow [gpm]" level={3}>
                <div>
                  <RtValue orientation="horizontal" title="Sp" rtValue={realtime[codes.sprayCooling.zone4.flow.sp]} style={{ color: "blue" }} />
                  <RtValue orientation="horizontal" title="Actual" rtValue={realtime[codes.sprayCooling.zone4.flow.actual]} style={{ color: "green" }} />
                </div>
              </Section>
              <Section title="Pressure [psi]" level={3}>
                <RtValue orientation="horizontal" title="Sp" rtValue={realtime[codes.sprayCooling.zone4.pressure]} style={{ color: "green" }} />
                <span/>
              </Section>
            </div>
          </Section>
        </div>
      </Section>
      <Section title="Withdrawal & TCM" level={1}>
        <div className="display-grid gap-1" style={{ gridTemplateRows: "auto auto" }}>
          <div className="display-grid" style={{ gridTemplateRows: "1fr auto" }}>
            <div>
              <AcquisitionChartWrapper
                realtime={realtime}
                component={dashboard.layout.components[1]}
                dashboard={dashboard}
                runtime={true}
                parentSize={{ width: width, height: 200 }}
                setDashboard={() => {}}
                schemas={schemas}/>
            </div>
            <div className="display-grid" style={{ gridTemplateRows: "auto auto 1fr" }}>
              <RtValue orientation="vertical" title="Withdrawal" rtValue={realtime[codes.cutter.withdrawal]} style={{ color: "green" }} />
              <RtValue orientation="vertical" title="Straightener" rtValue={realtime[codes.cutter.straightener]} style={{ color: "yellow" }} />
            </div>
          </div>
          <Section title="Length" level={2}>
            <div className="display-grid" style={{ gridTemplateColumns: "1fr 1fr 1fr 1fr" }}>
              <RtValue orientation="vertical" title="Act. Length [inch]" rtValue={realtime[codes.cutter.length.actual]} style={{ color: "green" }} />
              <RtValue orientation="vertical" title="Total Casted Length [ft]" rtValue={realtime[codes.cutter.length.totalCasted]} style={{ color: "green" }} />
              <RtValue orientation="vertical" title="Remaining Length [inch]" rtValue={realtime[codes.cutter.length.remaining]} style={{ color: "green" }} />
              <RtValue orientation="vertical" title="Total Cuts [#]" rtValue={realtime[codes.cutter.length.totalCut]} style={{ color: "green" }} />
            </div>
          </Section>
        </div>
      </Section>
    </Section>
  );
}

export default StrandComponent;